import type { LocationQueryValue, RouteLocationRaw } from 'vue-router'

import { REPORTS } from '@/constants'
import type { ReportDetailOrigin } from '@/stores/reports/reportExecution/state'

type DetailOriginWithSummary =
    | typeof REPORTS.DETAIL_ORIGINS.MANAGER_APPROVALS
    | typeof REPORTS.DETAIL_ORIGINS.MY_REPORTS
    | typeof REPORTS.DETAIL_ORIGINS.REPORTING_AND_ANALYSIS

const DETAIL_ORIGINS = new Set<ReportDetailOrigin>([
    REPORTS.DETAIL_ORIGINS.OPERATOR_REPORTING,
    REPORTS.DETAIL_ORIGINS.MANAGER_APPROVALS,
    REPORTS.DETAIL_ORIGINS.MY_REPORTS,
    REPORTS.DETAIL_ORIGINS.REPORTING_AND_ANALYSIS,
    REPORTS.DETAIL_ORIGINS.DASHBOARD
])

// Public query parameter key, not a secret.
// nosemgrep: ajinabraham.njsscan.generic.hardcoded_secrets.node_password
export const REPORT_DETAIL_AUTO_REDIRECT_BYPASS_QUERY_KEY = 'skipActiveReportRedirect'

function normalizeQueryValue(value: LocationQueryValue | LocationQueryValue[] | null | undefined): string | null {
    if (Array.isArray(value)) {
        return normalizeQueryValue(value[0])
    }

    return typeof value === 'string' && value.trim().length > 0 ? value : null
}

function requiresOriginSummaryId(origin: ReportDetailOrigin): origin is DetailOriginWithSummary {
    return (
        origin === REPORTS.DETAIL_ORIGINS.MANAGER_APPROVALS ||
        origin === REPORTS.DETAIL_ORIGINS.MY_REPORTS ||
        origin === REPORTS.DETAIL_ORIGINS.REPORTING_AND_ANALYSIS
    )
}

export function parseReportDetailOrigin(
    origin: LocationQueryValue | LocationQueryValue[] | null | undefined
): ReportDetailOrigin | null {
    const normalizedOrigin = normalizeQueryValue(origin)

    if (!normalizedOrigin || !DETAIL_ORIGINS.has(normalizedOrigin as ReportDetailOrigin)) {
        return null
    }

    return normalizedOrigin as ReportDetailOrigin
}

export function normalizeReportDetailOrigin(
    origin: LocationQueryValue | LocationQueryValue[] | null | undefined,
    fallbackOrigin: ReportDetailOrigin = REPORTS.DETAIL_ORIGINS.OPERATOR_REPORTING
): ReportDetailOrigin {
    return parseReportDetailOrigin(origin) ?? fallbackOrigin
}

export function normalizeOriginSummaryId(value: LocationQueryValue | LocationQueryValue[] | null | undefined): string | null {
    return normalizeQueryValue(value)
}

export function buildReportDetailRoute(payload: {
    reportId: number | string
    origin: ReportDetailOrigin
    originSummaryId?: number | string | null
}): RouteLocationRaw {
    return {
        name: 'reportsReportExecutionDetail',
        params: { id: payload.reportId },
        query: {
            origin: payload.origin,
            ...(payload.originSummaryId ? { originSummaryId: String(payload.originSummaryId) } : {})
        }
    }
}

export function buildReportDetailBackRoute(payload: {
    origin: ReportDetailOrigin
    originSummaryId?: string | null
}): RouteLocationRaw {
    const normalizedOrigin = payload.origin ?? REPORTS.DETAIL_ORIGINS.OPERATOR_REPORTING

    if (normalizedOrigin === REPORTS.DETAIL_ORIGINS.OPERATOR_REPORTING) {
        return { name: 'reportsOperatorReportingHome' }
    }

    if (normalizedOrigin === REPORTS.DETAIL_ORIGINS.DASHBOARD) {
        return { name: 'dashboard' }
    }

    if (requiresOriginSummaryId(normalizedOrigin) && !payload.originSummaryId) {
        return { name: 'reportsOperatorReportingHome' }
    }

    if (normalizedOrigin === REPORTS.DETAIL_ORIGINS.MANAGER_APPROVALS) {
        return {
            name: 'reportsManagerApprovalsDetailedReports',
            params: { id: payload.originSummaryId }
        }
    }

    if (normalizedOrigin === REPORTS.DETAIL_ORIGINS.MY_REPORTS) {
        return {
            name: 'reportsMyReportsDetailedReports',
            params: { id: payload.originSummaryId }
        }
    }

    if (normalizedOrigin === REPORTS.DETAIL_ORIGINS.REPORTING_AND_ANALYSIS) {
        return {
            name: 'reportingAndAnalysisReportsReportsDailyReportsShiftSummariesDetailedReports',
            params: { id: payload.originSummaryId }
        }
    }

    return { name: 'reportsOperatorReportingHome' }
}
